import type { FormInstance } from "antd";
import { Checkbox, Col, DatePicker, Form, Input, InputNumber, Row, Select, Switch } from "antd";
import type { Dayjs } from "dayjs";
import { OrganizationTreeSelect } from "@/modules/organization-cross-access/components/OrganizationTreeSelect";
import { useTaskTypes } from "@/modules/task-types/hooks/useTaskTypes";
import type { TaskLocationDraft } from "@/modules/tasks/components/TaskLocationListInput";
import { TaskTargetFields } from "@/modules/tasks/components/TaskTargetFields";
import { TASK_PRIORITY_OPTIONS } from "@/modules/tasks/constants";
import type { TaskPriority, TaskTargetType } from "@/modules/tasks/types";
import { RECURRENCE_OPTIONS, WEEKDAY_OPTIONS } from "../constants";
import type { TaskRecurrence } from "../types";

// AntD form values: dates come back as Dayjs (converted to strings at submit),
// locations as in-progress drafts — matching TaskForm's own convention.
export interface TaskScheduleFormValues {
	title: string;
	organizationId: string;
	brigadeId: string;
	taskTypeId: string;
	targetType: TaskTargetType;
	locations?: TaskLocationDraft[];
	plantingIds?: string[];
	priority?: TaskPriority;
	recurrence: TaskRecurrence;
	intervalCount?: number;
	weekdays?: number[];
	dayOfMonth?: number;
	timeOfDay?: Dayjs | null;
	startDate?: Dayjs | null;
	endDate?: Dayjs | null;
	description: string;
	isActive?: boolean;
}

interface TaskScheduleFormProps {
	form: FormInstance<TaskScheduleFormValues>;
	isEdit?: boolean;
}

export function TaskScheduleForm({ form, isEdit }: TaskScheduleFormProps) {
	const selectedOrgId = Form.useWatch("organizationId", form);
	const recurrence = Form.useWatch("recurrence", form);

	const { data: taskTypesData } = useTaskTypes({ page: 1, limit: 100 });
	const taskTypes = taskTypesData?.data?.items ?? [];

	return (
		<>
			<Row gutter={16}>
				<Col span={24}>
					<Form.Item
						name="title"
						label="Sarlavha"
						rules={[
							{ required: true, message: "Sarlavhani kiriting" },
							{ max: 255, message: "Maksimum 255 ta belgi" },
						]}
					>
						<Input placeholder="Masalan: Ko'chani supirish" />
					</Form.Item>
				</Col>
			</Row>
			<Row gutter={16}>
				<Col span={12}>
					<Form.Item
						name="organizationId"
						label="Tashkilot"
						rules={[{ required: true, message: "Tashkilotni tanlang" }]}
					>
						<OrganizationTreeSelect disabled={isEdit} />
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						name="taskTypeId"
						label="Topshiriq turi"
						rules={[{ required: true, message: "Topshiriq turini tanlang" }]}
					>
						<Select
							placeholder="Topshiriq turini tanlang"
							showSearch
							optionFilterProp="label"
							options={taskTypes.map((t) => ({ value: t.id, label: t.name }))}
						/>
					</Form.Item>
				</Col>
			</Row>

			{!isEdit && <TaskTargetFields form={form} organizationId={selectedOrgId} />}

			<Row gutter={16}>
				<Col span={12}>
					<Form.Item name="priority" label="Ustuvorlik">
						<Select options={TASK_PRIORITY_OPTIONS} placeholder="Tanlang" />
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						name="recurrence"
						label="Takrorlanish turi"
						rules={[{ required: true, message: "Takrorlanish turini tanlang" }]}
					>
						<Select options={RECURRENCE_OPTIONS} placeholder="Tanlang" />
					</Form.Item>
				</Col>
			</Row>
			<Row gutter={16}>
				<Col span={12}>
					<Form.Item
						name="intervalCount"
						label="Har necha marta"
						tooltip="Masalan: 2 = har 2 kunda/haftada/oyda bir marta"
						rules={[{ required: true, message: "Qiymatni kiriting" }]}
					>
						<InputNumber className="w-full" min={1} placeholder="1" />
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						name="timeOfDay"
						label="Vaqti"
						rules={[{ required: true, message: "Vaqtni tanlang" }]}
					>
						<DatePicker.TimePicker className="w-full" format="HH:mm" />
					</Form.Item>
				</Col>
			</Row>
			{recurrence === "WEEKLY" && (
				<Row gutter={16}>
					<Col span={24}>
						<Form.Item
							name="weekdays"
							label="Hafta kunlari"
							rules={[{ required: true, message: "Kamida bitta kunni tanlang" }]}
						>
							<Checkbox.Group options={WEEKDAY_OPTIONS} />
						</Form.Item>
					</Col>
				</Row>
			)}
			{recurrence === "MONTHLY" && (
				<Row gutter={16}>
					<Col span={12}>
						<Form.Item
							name="dayOfMonth"
							label="Oyning kuni"
							rules={[{ required: true, message: "Kunni tanlang" }]}
						>
							<InputNumber className="w-full" min={1} max={31} placeholder="1-31" />
						</Form.Item>
					</Col>
				</Row>
			)}
			<Row gutter={16}>
				<Col span={12}>
					<Form.Item
						name="startDate"
						label="Boshlanish sanasi"
						rules={[{ required: true, message: "Sanani tanlang" }]}
					>
						<DatePicker className="w-full" format="DD.MM.YYYY" disabled={isEdit} />
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item name="endDate" label="Tugash sanasi (ixtiyoriy)">
						<DatePicker className="w-full" format="DD.MM.YYYY" />
					</Form.Item>
				</Col>
			</Row>
			<Row gutter={16}>
				<Col span={24}>
					<Form.Item
						name="description"
						label="Izoh"
						rules={[
							{ required: true, message: "Izohni kiriting" },
							{ max: 2000, message: "Maksimum 2000 ta belgi" },
						]}
					>
						<Input.TextArea rows={3} placeholder="Qisqacha izoh" />
					</Form.Item>
				</Col>
			</Row>
			<Form.Item name="isActive" label="Faol" valuePropName="checked">
				<Switch />
			</Form.Item>
		</>
	);
}
